import ChartBarLineIcon from '@atlaskit/icon-lab/core/chart-bar-line';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import { useCallback, useMemo } from 'react';
import {
	type BackendPlacementMessage,
	type FrontendPlacementMessage,
	noValidation,
	type BackendMessageStageInput,
} from '@post-office/placement-common-v2';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--post-office-team/rovo-nudge';
import { NudgeDefinition } from '@post-office/placements--post-office-team/rovo-nudge/nudge-definition';

import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { type MessageTemplateContext } from '../../../index';
import {
	type CreatePlacementMessageConfig,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
	type AnyFrontendStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: () => ({
			goalMetadata: {
				primaryGoal: {
					goalId: 'clickthrough',
				},
			},
		}),
	},
	hydrate: {
		// Subscription / GraphQL payloads may include extra or mistyped fields (e.g. scopes).
		// Validate only after narrowing to the shape render uses.
		validate: noValidation<MessageTemplateContext>(),
		stage: async ({ message }: BackendMessageStageInput<MessageTemplateContext>) => ({
			title: message.context.title,
			description: message.context.description,
		}),
	},
}));

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation(),
		Component: ({ title, description }: MessageTemplateContext) => {
			const icon = useCallback(() => <ChartBarLineIcon size="medium" label={title} />, [title]);
			const nudgeContext = useMemo(() => ({ id: 'live-and-persisted-test-message' }), []);
			return (
				<NudgeDefinition
					title={title}
					description={description}
					icon={icon}
					handleType={HandleActionType.Chat}
					staticPrompt={`Tell me more about: ${title}`}
					starterType="static"
					nudgeContext={nudgeContext}
				/>
			);
		},
	},
});
